import { Box, Card, CardContent, CardHeader, Drawer, IconButton, List, ListItem, ListItemText, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { generateClient } from "aws-amplify/api";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { PageAppBar } from "../../components/pageAppBar";
import { esQuery } from "../../graphql/queries";
import { cardStyle } from "../../theme";
import { CopyAllOutlined } from "@mui/icons-material";


export function Licensing() {
    const client = generateClient();
    const lastStartDate = useRef(null);
    const lastEndDate = useRef(null);
    const [startDate, setStartDate] = useState(moment().startOf('month').toISOString());
    const [endDate, setEndDate] = useState(moment().endOf('month').toISOString());
    const [tenantData, setTenantData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [agentDetails, setAgentDetails] = useState(null);

    const monthDays = {
        January: 31,
        // Handle leap years for February
        February: (moment().isLeapYear() ? 29 : 28),
        March: 31,
        April: 30,
        May: 31,
        June: 30,
        July: 31,
        August: 31,
        September: 30,
        October: 31,
        November: 30,
        December: 31
    };


    useEffect(() => {
        if (startDate && endDate && (lastStartDate.current !== startDate || lastEndDate.current !== endDate)) {
            lastStartDate.current = startDate;
            lastEndDate.current = endDate;
            getTodaysLicenseCounts();
        }
    }, [startDate]);

    function getQuery() {
        return {
            "aggs": {
                "date": {
                    "date_histogram": {
                        "field": "createdAt",
                        "calendar_interval": "day",
                        "time_zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
                        "min_doc_count": 1
                    },
                    "aggs": {
                        "tenant": {
                            "terms": {
                                "field": "tenant.keyword",
                                "size": 500
                            },
                            "aggs": {
                                "type_count": {
                                    "cardinality": {
                                        "field": "agent.keyword"
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "query": {
                "bool": {
                    "must": [
                        {
                            "term": {
                                "type.keyword": "Dial"
                            }
                        },
                        {
                            "range": {
                                "createdAt": {
                                    "gte": startDate,
                                    "lte": endDate
                                }
                            }
                        }
                    ]
                }
            },
            "size": 0
        }
    }

    function getAgentDetailsQuery(tenant, dayOfMonth) {
        return {
            "aggs": {
                "agents": {
                    "terms": {
                        "field": "agent.keyword",
                        "size": 500
                    }
                }
            },
            "query": {
                "bool": {
                    "must": [
                        {
                            "term": {
                                "tenant.keyword": tenant
                            }
                        },
                        {
                            "term": {
                                "type.keyword": "Dial"
                            }
                        },
                        {
                            "range": {
                                "createdAt": {
                                    "gte": moment(startDate).startOf('month').set('date', dayOfMonth).startOf('day').toISOString(),
                                    "lte": moment(startDate).startOf('month').set('date', dayOfMonth).endOf('day').toISOString()
                                }
                            }
                        }
                    ]
                }
            },
            "size": 0
        }
    }


    async function getTodaysLicenseCounts() {
        try {
            setLoading(true);
            const query = await client.graphql({
                query: esQuery,
                variables: {
                    model: 'activitylog-*',
                    action: "_search",
                    query: JSON.stringify(getQuery())
                }
            });

            const parsedResult = JSON.parse(query.data.esQuery);
            const data = parsedResult.aggregations;
            const tenantData = [];
            for (const day of data.date.buckets) {
                // date_histogram returns key_as_string as ISO date; extract day of month
                const dayOfMonth = moment(day.key_as_string || day.key).date();
                for (const tenant of day.tenant.buckets) {
                    const existingTenant = tenantData.find(t => t.tenant === tenant.key);
                    if (existingTenant) {
                        if (tenant.type_count.value > existingTenant.maxAgents) {
                            existingTenant.maxAgents = tenant.type_count.value;
                        }
                        existingTenant.agentCountData.push({
                            day: dayOfMonth,
                            count: tenant.type_count.value
                        });
                        existingTenant.agentCountData.sort((a, b) => a.day - b.day);
                    } else {
                        tenantData.push({
                            tenant: tenant.key,
                            maxAgents: tenant.type_count.value,
                            agentCountData: [{
                                day: dayOfMonth,
                                count: tenant.type_count.value
                            }]
                        });
                    }
                }
            }
            console.log("Processed tenant data:", tenantData);
            setTenantData(tenantData);

        } catch (error) {
            console.error("Error fetching license counts:", error);
        }
        setLoading(false);
    }

    const handleMonthChange = (event) => {
        const selectedMonth = event.target.value;
        const startOfMonth = moment(selectedMonth).startOf('month').toISOString();
        const endOfMonth = moment(selectedMonth).endOf('month').toISOString();
        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    }

    const getDateFullAgentDetails = async (tenant, dayOfMonth) => {
        try {
            const agentQuery = await client.graphql({
                query: esQuery,
                variables: {
                    model: 'activitylog-*',
                    action: "_search",
                    query: JSON.stringify(getAgentDetailsQuery(tenant, dayOfMonth))
                }
            });

            const parsedResult = JSON.parse(agentQuery.data.esQuery);
            setAgentDetails(parsedResult.aggregations.agents.buckets);
        } catch (error) {
            console.error("Error fetching agent details:", error);
        }
    }


    return (
        <Box>
            <PageAppBar title="Licensing" />
            <Card variant="outlined" sx={cardStyle}>
                <CardHeader action={
                    <Select
                        label="Select Month"
                        disabled={loading}
                        value={moment(startDate).format('YYYY-MM')}
                        onChange={handleMonthChange}
                        renderValue={(value) => moment(value).format('MMMM YYYY')}
                        sx={{ minWidth: 200 }}
                    >
                        {Array.from({ length: 12 }).map((_, index) => {
                            const month = moment().subtract(index, 'months');
                            return (
                                <MenuItem key={month.format('YYYY-MM')} value={month.format('YYYY-MM')}>
                                    {month.format('MMMM YYYY')}
                                </MenuItem>
                            );
                        })}
                    </Select>
                } />
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Typography variant="h6">
                                        Tenant
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="h6">
                                        Max Agents
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant="h6">
                                        Agent Count Data
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tenantData.map((tenant) => (
                                <TableRow key={tenant.tenant}>
                                    <TableCell>
                                        <Typography variant="h6">
                                            {tenant.tenant}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="h6">
                                            {tenant.maxAgents}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            <LineChart
                                                xAxis={[{ data: Array.from({ length: monthDays[moment(startDate).format('MMMM')] }, (_, i) => i + 1) }]}
                                                series={[{
                                                    data: Array.from({ length: monthDays[moment(startDate).format('MMMM')] }, (_, i) => {
                                                        const dayData = tenant.agentCountData.find(d => d.day == i + 1);
                                                        return dayData ? dayData.count : 0;
                                                    }),
                                                    name: tenant.tenant,
                                                    area: true,
                                                    baseline: 0,
                                                    showMark: ({ index }) => {
                                                        const dayData = tenant.agentCountData.find(d => d.day == index + 1);
                                                        return dayData && dayData.count == tenant.maxAgents;
                                                    },
                                                    curve: "catmullRom"
                                                }]}
                                                height={150}
                                                width={400}
                                                leftAxis={null}
                                                bottomAxis={null}
                                                onMarkClick={() => { }}
                                                onAxisClick={(_, data) => {
                                                    getDateFullAgentDetails(tenant.tenant, data.axisValue);
                                                }}
                                                grid={{ vertical: false, horizontal: false }}
                                                slotProps={{
                                                    line: {
                                                        style: {
                                                            stroke: '#6BAE3F',
                                                            strokeWidth: 2
                                                        }
                                                    },
                                                    area: {
                                                        style: {
                                                            fill: '#6BAE3F',
                                                            opacity: 0.1
                                                        }
                                                    },
                                                    mark: {
                                                        color: '#6BAE3F',

                                                    },

                                                }}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            {/* Drawer */}
            <Box
                sx={{
                    width: { sm: 450 },
                    flexShrink: { sm: 0 },
                }}
            >
                <Drawer
                    variant="temporary"
                    open={agentDetails}
                    anchor="right"
                    onClose={() => setAgentDetails(null)}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: 450,
                            padding: '15px',
                        },
                    }}
                >
                    <Card variant="outlined" sx={cardStyle}>
                        <CardHeader title="Agent Details" action={
                            <IconButton onClick={() => {
                                if (agentDetails) {
                                    // Join to newline separated list of agent keys
                                    const agentList = agentDetails.map(agent => agent.key).join('\n');
                                    navigator.clipboard.writeText(agentList);
                                }
                            }}>
                                <CopyAllOutlined color="primary" />
                            </IconButton>
                        } />
                        <CardContent>
                            <List>
                                {agentDetails ? (
                                    agentDetails.map((agent) => (
                                        <ListItem key={agent.key}>
                                            <ListItemText primary={agent.key} />
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>
                                        <ListItemText primary="No agent details available for this date." />
                                    </ListItem>
                                )}
                            </List>
                        </CardContent>
                    </Card>
                </Drawer>
            </Box>
        </Box >
    )
}
